'use client'
import Image from 'next/image'
import Link from 'next/link'
import { useRef, useState } from 'react'
import copy from '../_content/site-copy.json'
import type { TimelineModel } from '../_lib/timeline'
const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})
const maxAmount = 5e11
function DealMark({ amount }: { amount: number | null }) {
  const diameter =
    amount === null ? 6 : Math.max(4, Math.sqrt(amount / maxAmount) * 38)
  return (
    <span
      className="museum-deal-dot"
      data-unknown={amount === null}
      style={{ width: diameter, height: diameter }}
    />
  )
}
export function Timeline({ model }: { model: TimelineModel }) {
  const scroller = useRef<HTMLDivElement>(null)
  const [selection, setSelection] = useState<{
    id: string
    kind: 'artifact' | 'event'
    pinned: boolean
  } | null>(null)
  const artifact =
    selection?.kind === 'artifact'
      ? model.artifacts.find(({ item }) => item.id === selection.id)?.item
      : undefined
  const event =
    selection?.kind === 'event'
      ? [...model.events.entries, ...model.deals.entries].find(
          ({ event }) => event.id === selection.id,
        )?.event
      : undefined
  const point = (id: string, kind: 'artifact' | 'event') => {
    setSelection((current) =>
      current?.pinned ? current : { id, kind, pinned: false },
    )
  }
  const pin = (id: string) =>
    setSelection((current) =>
      current?.id === id && current.pinned
        ? null
        : { id, kind: 'event', pinned: true },
    )
  return (
    <div
      className="museum-timeline"
      onKeyDown={(event) => {
        if (event.key === 'Escape') setSelection(null)
      }}
    >
      <div className="museum-index-toolbar">
        <h1>{copy.timeline.title}</h1>
        <label className="museum-year-jump">
          <span className="sr-only">{copy.item.year}</span>
          <select
            defaultValue="1890"
            onChange={(event) => {
              const year = model.years.find(
                (year) => year.year === Number(event.target.value),
              )
              if (year)
                scroller.current?.scrollTo({ left: year.x, behavior: 'smooth' })
            }}
          >
            {model.years.map(({ year }) =>
              year % 10 === 0 ? <option key={year}>{year}</option> : null,
            )}
          </select>
        </label>
      </div>
      <div
        className="museum-timeline-detail"
        data-selected={Boolean(selection)}
        aria-live="polite"
      >
        {selection && (
          <button
            type="button"
            className="museum-detail-close"
            onClick={() => setSelection(null)}
            aria-label={copy.timeline.title}
          >
            ×
          </button>
        )}
        {artifact ? (
          <h2 className="museum-hover-title" key={artifact.id}>
            {artifact.year} — {artifact.title}
          </h2>
        ) : event ? (
          <div key={event.id}>
            <h2 className="museum-hover-title">
              {event.date} — {event.title}
            </h2>
            <p>{event.note}</p>
            {'track' in event && event.track === 'deals' && (
              <span className="museum-label">
                {event.amountUsd === null
                  ? copy.timeline.amountUnknown
                  : currency.format(event.amountUsd)}
              </span>
            )}
            <div className="museum-timeline-sources">
              {event.sources.map((source) => (
                <a
                  key={source.url}
                  href={source.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {source.publisher} ↗
                </a>
              ))}
            </div>
          </div>
        ) : (
          <p className="museum-timeline-hint">{copy.timeline.hint}</p>
        )}
      </div>
      <div
        className="museum-timeline-scroll"
        tabIndex={0}
        role="region"
        aria-label={copy.timeline.title}
        ref={scroller}
        data-lenis-prevent
        onMouseLeave={() => {
          setSelection((current) => (current?.pinned ? current : null))
        }}
      >
        <div
          className="museum-timeline-canvas"
          style={{ width: model.width }}
          data-hover={selection?.kind === 'artifact' ? 'true' : undefined}
        >
          <div className="museum-decade-rules" aria-hidden="true">
            {model.years.map(({ year, x }) =>
              year % 10 === 0 ? (
                <div key={year} style={{ left: x }}>
                  <span>{year}</span>
                </div>
              ) : null,
            )}
          </div>
          <section className="museum-time-track museum-time-artifacts">
            <h2>{copy.timeline.tracks.artifacts}</h2>
            <div className="museum-track-marks" style={{ height: 280 }}>
              {model.artifacts.map(({ item, x, y }) => (
                <Link
                  className="museum-time-artifact"
                  key={item.id}
                  href={`/museum/items/${item.id}`}
                  style={{ left: x, top: y }}
                  data-active={artifact?.id === item.id}
                  onPointerEnter={(e) => {
                    if (e.pointerType === 'mouse') point(item.id, 'artifact')
                  }}
                  onFocus={() => point(item.id, 'artifact')}
                >
                  <Image
                    src={item.image.src}
                    alt={item.title}
                    width={item.image.width}
                    height={item.image.height}
                    sizes="72px"
                  />
                </Link>
              ))}
            </div>
          </section>
          <section className="museum-time-track">
            <h2>{copy.timeline.tracks.events}</h2>
            <div
              className="museum-track-marks"
              style={{ height: model.events.height }}
            >
              {model.events.entries.map(({ event, x, y }) => (
                <button
                  className="museum-event-mark"
                  key={event.id}
                  type="button"
                  style={{ left: x, top: y }}
                  aria-label={`${event.date}: ${event.title}`}
                  aria-pressed={selection?.id === event.id && selection.pinned}
                  onClick={() => pin(event.id)}
                  onFocus={() => point(event.id, 'event')}
                  onPointerEnter={(e) => {
                    if (e.pointerType === 'mouse') point(event.id, 'event')
                  }}
                >
                  <span className="museum-event-tick" />
                </button>
              ))}
            </div>
          </section>
          <section className="museum-time-track">
            <h2>{copy.timeline.tracks.deals}</h2>
            <div
              className="museum-track-marks"
              style={{ height: model.deals.height }}
            >
              {model.deals.entries.map(({ event, x, y }) => (
                <button
                  className="museum-event-mark museum-deal-mark"
                  key={event.id}
                  type="button"
                  style={{ left: x, top: y }}
                  aria-label={`${event.date}: ${event.title}`}
                  aria-pressed={selection?.id === event.id && selection.pinned}
                  onClick={() => pin(event.id)}
                  onFocus={() => point(event.id, 'event')}
                  onPointerEnter={(e) => {
                    if (e.pointerType === 'mouse') point(event.id, 'event')
                  }}
                >
                  <DealMark amount={event.amountUsd} />
                </button>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
